import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../../../interceptors/api";
import { useLayout } from "../../../LayoutContext";
import { DeviceStoryDto } from "../../dto/DeviceStoryDto";
import { UserStoryDto } from "../../dto/UserStoryDto";
import "./styles/EchoShell.css";

const INACTIVITY_MS = 5 * 60 * 1000; // 5 minutes

interface PlanetInfo {
  x: number; // percent
  y: number; // percent
  size: number; // px
  story: UserStoryDto;
}

const earth: UserStoryDto = {
  id: 99998,
  titleEN: "Back to Earth",
  titleDE: "Back to Earth",
  visualUrl: "/assets/themes/echo/earth.png",
  textDE: "",
  textEN: "",
};

const share: UserStoryDto = {
  id: 99999,
  titleEN: "Create Your Planet",
  titleDE: "Create Your Planet",
  visualUrl: "/assets/themes/echo/echo_share-story_qr.png",
  textDE: "",
  textEN: "",
};

function EchoDotShell() {
  const navigate = useNavigate();
  const { setExhibitionMode } = useLayout();

  // data
  const [userStories, setUserStories] = useState<UserStoryDto[]>([]);
  const [deviceStory, setDeviceStory] = useState<DeviceStoryDto | null>(null);

  // packed planets (with sizes)
  const [planets, setPlanets] = useState<PlanetInfo[]>([]);
  const prevIdsRef = useRef<number[]>([]);

  // audio
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  // inactivity
  const inactivityTimer = useRef<number>();

  const resetTimer = useCallback(() => {
    window.clearTimeout(inactivityTimer.current);
    inactivityTimer.current = window.setTimeout(
      () => navigate("/exhibits/werkschau", { replace: true }),
      INACTIVITY_MS,
    );
  }, [navigate]);

  // exhibition mode
  useEffect(() => {
    setExhibitionMode(true);
    return () => {
      setExhibitionMode(false);
    };
  }, [setExhibitionMode]);

  // bind inactivity
  useEffect(() => {
    const events = ["mousemove", "mousedown", "keydown", "touchstart"];
    events.forEach((e) => window.addEventListener(e, resetTimer));
    resetTimer();
    return () => {
      window.clearTimeout(inactivityTimer.current);
      events.forEach((e) => window.removeEventListener(e, resetTimer));
    };
  }, [resetTimer]);

  // fetch user stories
  useEffect(() => {
    let mounted = true;

    const fetchStories = () =>
      api
        .get<UserStoryDto[]>("/api/user-story/device/3")
        .then((res) => {
          if (!mounted) return;
          const userStories: UserStoryDto[] = [earth, share, ...res.data];
          setUserStories(userStories);
        })
        .catch((err) => {
          console.error("Failed to load stories:", err);
        });

    fetchStories();

    const id = window.setInterval(fetchStories, 60_000);
    return () => {
      mounted = false;
      window.clearInterval(id);
    };
  }, []);

  useEffect(() => {
    const newIds = userStories.map((s) => s.id).sort((a, b) => a - b);
    const oldIds = prevIdsRef.current;

    // simple array‐equality check (same length, same members)
    const same =
      newIds.length === oldIds.length &&
      newIds.every((id, i) => id === oldIds[i]);

    if (!same) {
      // only regenerate if something really changed
      generatePlanets(userStories);
      // stash the new IDs for next time
      prevIdsRef.current = newIds;
    }
  }, [userStories]);

  useEffect(() => {
    let mounted = true;

    const fetchDeviceStory = () =>
      api
        .get<DeviceStoryDto>("/api/device-story/device/3/exhibition/1")
        .then((res) => mounted && setDeviceStory(res.data))
        .catch((err) => {
          if (!mounted) return;
          console.error("Error loading device story", err);
        });

    fetchDeviceStory();

    const id = window.setInterval(fetchDeviceStory, 60_000);
    return () => {
      mounted = false;
      window.clearInterval(id);
    };
  }, []);

  const generatePlanets = useCallback((userStories: UserStoryDto[]) => {
    // … exactly your placement logic, but always overwrite `setPlanets` …
    const containerPx = Math.min(window.innerWidth, window.innerHeight) * 0.8;
    const pxToPct = 100 / containerPx;
    const echoRadiusPct = (200 / 2 + 20) * pxToPct;
    const triesPer = 220;
    const placed: PlanetInfo[] = [];

    userStories.forEach((story) => {
      const size = 130 + Math.random() * 20;
      const radiusPct = (size / 2 + 20) * pxToPct;
      const minPct = radiusPct;
      const maxPct = 100 - radiusPct;
      let x = 50,
        y = 50,
        ok = false;

      for (let t = 0; t < triesPer; t++) {
        x = minPct + Math.random() * (maxPct - minPct);
        y = minPct + Math.random() * (maxPct - minPct);
        // avoid center dot
        if (Math.hypot(x - 50, y - 50) < echoRadiusPct + radiusPct) continue;
        // avoid other planets
        if (
          !placed.some((p) => {
            const rA = (p.size / 2 + 20) * pxToPct;
            return Math.hypot(p.x - x, p.y - y) < rA + radiusPct;
          })
        ) {
          ok = true;
          break;
        }
      }

      placed.push({ x, y, size, story });
    });

    setPlanets(placed);
  }, []);

  // pack planets once
  useEffect(() => {
    if (!userStories.length || planets.length) return;

    // figure out how many % == 1px
    const containerPx = Math.min(window.innerWidth, window.innerHeight) * 0.8;
    const pxToPct = 100 / containerPx;

    // echo dot is 200px, we give it a 20px pad
    const echoRadiusPct = (200 / 2 + 20) * pxToPct;
    // max user radius . . . + 20px pad?
    const maxUserRadiusPct = (150 / 2 + 20) * pxToPct;

    const placed: PlanetInfo[] = [];
    const triesPer = 220;

    userStories.forEach((story) => {
      // random user‐planet size 130–150px
      const size = 130 + Math.random() * 20;
      const radiusPct = (size / 2 + 30) * pxToPct; // +20px pad

      const minPct = radiusPct;
      const maxPct = 100 - radiusPct;

      let x = 50,
        y = 50,
        ok = false;
      for (let t = 0; t < triesPer; t++) {
        x = minPct + Math.random() * (maxPct - minPct);
        y = minPct + Math.random() * (maxPct - minPct);

        // 1) keep well outside the echo‐dot
        const dx0 = x - 50,
          dy0 = y - 50;
        if (Math.hypot(dx0, dy0) < echoRadiusPct + radiusPct) continue;

        // 2) keep well outside any already‐placed planet
        let collision = false;
        for (let p of placed) {
          const dx = p.x - x,
            dy = p.y - y;
          const dist = Math.hypot(dx, dy);
          const otherR = (p.size / 2 + 30) * pxToPct; // same pad
          if (dist < otherR + radiusPct) {
            collision = true;
            break;
          }
        }
        if (!collision) {
          ok = true;
          break;
        }
      }
      // fall back anyway
      placed.push({ x, y, size, story });
    });

    setPlanets(placed);
  }, [userStories, planets.length]);

  // audio controls
  const toggleAudio = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      //a.currentTime = 0;
      a.play();
      setPlaying(true);
    }
  };

  const onEnded = () => setPlaying(false);

  const handleBackToStart = () => {
    navigate("/exhibits/werkschau", { replace: true });
  };

  return (
    <div className="echo-galaxy-shell">
      <div className="galaxy-background" />

      <div className="galaxy-container">
        {planets.map((p) => {
          const { id, visualUrl, titleEN } = p.story;
          // constants from your file:
          const EARTH_ID = 99998;
          const SHARE_ID = 99999;

          // build up your class list
          const classes = ["planet"];
          if (id === EARTH_ID) classes.push("earth-planet");
          else if (id === SHARE_ID) classes.push("share-planet");
          else classes.push("user-planet");

          // decide onClick only for earth
          const onClick = id === EARTH_ID ? handleBackToStart : undefined;

          return (
            <div
              key={id}
              className={classes.join(" ")}
              onClick={onClick}
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                width: `${p.size}px`,
                height: `${p.size}px`,
              }}
            >
              <img src={visualUrl} alt="" />
              <span className="planet-label">{titleEN}</span>
            </div>
          );
        })}

        {deviceStory && (
          <>
            <div
              className="planet device-planet"
              onClick={toggleAudio}
              style={{
                left: "50%",
                top: "46%",
                width: "200px",
                height: "200px",
              }}
            >
              <img
                src="/assets/themes/echo/echo_dot.png"
                alt="Planet Echo Dot"
              />
              <span className="planet-label">EchoDot</span>
            </div>
            <audio
              ref={audioRef}
              src={deviceStory.audioUrl || undefined}
              onEnded={onEnded}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default EchoDotShell;
